'use client';

import { AssistantBubble } from './assistant-bubble';
import { AssistantOverlay } from './assistant-overlay';
import { AssistantProvider, useAssistant } from './assistant-provider';
import { Composer } from './composer';
import { ConversationView } from './conversation-view';
import { HistoryView } from './history-view';

/**
 * Everything the dashboard layout needs to mount, in one component.
 *
 * Mounted once in `app/(app)/layout.tsx` so the provider survives client
 * navigation: clicking a link in a reply must not restart the conversation.
 */
export function AssistantRoot() {
  return (
    <AssistantProvider>
      <AssistantBubble />
      <AssistantOverlay>
        <AssistantBody />
      </AssistantOverlay>
    </AssistantProvider>
  );
}

/**
 * R-P3: the overlay's header History button toggles `showHistory` (Task 8).
 * This branches the overlay body on it: the conversation history list (Task
 * 14) when it is set, the transcript and composer otherwise. The way back is
 * the same header button, which flips its label to "Back to the
 * conversation" while history shows.
 */
function AssistantBody() {
  const { showHistory, openConversation, conversationId, newConversation } = useAssistant();

  if (showHistory) {
    // `onOpen` is passed directly, not wrapped in
    // `(id) => void openConversation(id)`: that would discard the returned
    // promise before HistoryView ever sees it, so a rejection (conversation
    // deleted elsewhere, network drop) could never be caught. HistoryView
    // itself catches it and toasts.
    //
    // Deleting the conversation that is open has to clear the provider's id,
    // its sessionStorage key and the transcript still showing the deleted
    // blocks — which is exactly what "New chat" already does, so it is reused
    // here rather than widening the context with a second reset that would be
    // one more thing to keep in step.
    return (
      <HistoryView
        onOpen={openConversation}
        activeConversationId={conversationId}
        onActiveDeleted={newConversation}
      />
    );
  }

  return (
    <>
      <ConversationView />
      <ComposerConnected />
    </>
  );
}

function ComposerConnected() {
  const { status, streaming, send, stop } = useAssistant();
  return (
    <Composer
      status={status}
      streaming={streaming}
      onSend={(text) => void send(text)}
      onStop={stop}
    />
  );
}
